

<script lang="ts" setup>
import { useModalsStore } from '@/stores/modals';
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { usePurchaseStore } from '../../../stores/purchase';
import config from '@/config';
import { finishPaySafePayment } from '@/api/client/client_accounts.buy';
import { useUserStore } from '../../../stores/user';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { setLocale } from 'yup';
import countryPicker from '@/components/select/country-select.vue';
import i18n from '@/translations';
import { useMatomo } from '@/composables/useMatomo';
import { loadExternalScript } from '@/utils';
import countryJson from './country.json';
import filters from '@/filters';

const { trackEvent, trackGoal } = useMatomo();
const { clearCart } = usePurchaseStore();
const { payment_methods, cart, getCartList, getBonus } = storeToRefs(usePurchaseStore());
const { closeModal } = useModalsStore();

const ccIcon = ref(undefined);
const cardNumber = ref(undefined);
const cardAddon = ref(undefined);
const cardExpiry = ref(undefined);
const cardCVC = ref(undefined);
const isCardValid = ref(false);
const isBillingValid = ref(false);
const isFormValid = ref(false);
const isFinished = ref(false);
const isProcessing = ref(false);
const hasErrors = ref(false);
const country: any = ref(undefined);
const CLOSE_TIMEOUT_SEC = 5;
const activeValidation = ref(false);
const isSubmitting = ref(false);
const filter = ref('');
const merchantName = ref('Sensemakers B.V.');
const ENVIRONMENT_MODE = `${config.CCEnvironment}`; //LIVE or TEST
const CURRENCY_CODE = 'EUR';
const needsBillingInfo = ref(false);
const scenarioSelected: any = ref(null);
const currencyCode = ref('EUR');
let paysafeInstance: any;

const DEFAULT_COUNTRIES = countryJson.map( item => {
   item.name = i18n.global.t(item.name);
   return item;
});;

const countries = config.AllowedCreditcardCountries === 'all' ? DEFAULT_COUNTRIES : DEFAULT_COUNTRIES.filter((p) =>  config.AllowedCreditcardCountries.includes(p.code));

const isTesting = computed(() => {
    return ENVIRONMENT_MODE === 'TEST';
})

//set local for billing information..
setLocale({
    mixed: {
        default: 'form.field_invalid',
        required: 'form.field_required'
    },
    number: {
        min: ({ min }) => ({ key: 'form.field_too_short', values: { min } }),
        max: ({ max }) => ({ key: 'form.field_too_big', values: { max } })
    }
});

yup.addMethod(yup.string, 'isValidZipcode', function (errorMessage) {
    return this.test("isValidZipcode", errorMessage, function (value) {
        if (country.value && typeof country.value.code === 'string' && value) {
            const countryCode = country.value.code;
            if (postcodeValidatorExistsForCountry(countryCode)) {
                const result = postcodeValidator(value, countryCode);
                return result;
            } else {
                console.warn('No validator for country', countryCode);
                return true;
            }
        } else {
            return true;
        }
    });
});

const validate: any = computed(() => {
    return yup.object({
        firstName: yup.string().required().max(80),
        lastName: yup.string().required().max(80),
        street: yup.string().matches(/^(?:[A-Z] \d|[^\W\d_]{2,}\.?)(?:[- '’][^\W\d_]+\.?)*\s+[1-9]\d{0,3} ?[a-zA-Z]?(?: ?[/-] ?[1-9]\d{0,3} ?[a-zA-Z]?)?$/ , i18n.global.t('modals.paysafe.errors.address_invalid')).required().max(50),
        city: yup.string().required().max(40),
        stateUS: yup.string().when([],  {
            is: () => {
                return country.value && country.value.code === 'US';
            } ,
            then: yup.string().length(2).required(i18n.global.t('modals.paysafe.errors.state_required')),
            otherwise: yup.string().notRequired()
        }),
        stateCA: yup.string().when([], {
            is: () => {
                return country.value && country.value.code === 'CA';
            } ,
            then: yup.string().length(2).required(i18n.global.t('modals.paysafe.errors.state_required')),
            otherwise: yup.string().notRequired()
        }),
        zipcode: (yup.string().required().min(3).max(10) as any).isValidZipcode(i18n.global.t('modals.paysafe.errors.zipcode'))
    });
});

const { meta, values, errors, defineField } = useForm({
    validationSchema: validate
});

const [zipcode] = defineField('zipcode');
const [street] = defineField('street');
const [city] = defineField('city');
const [state] = defineField('state');
const [stateUS] = defineField('stateUS');
const [stateCA] = defineField('stateCA');
const [firstName] = defineField('firstName');
const [lastName] = defineField('lastName');

const emit = defineEmits(['onLoading']);

onBeforeMount(setAccount);
onMounted(load);

function load() { 
    setAccount();
    setupPaysafe();
}

function setAccount() {  
    //default test scenario if applicable
    const account = useUserStore().account;
    const code = account.country  ? account.country === 'cc' ? 'NL' : account.country.toUpperCase() : 'NL';
    country.value = countries.find((p: any) => p.code === code);

    //set currency
    if (!isTesting.value && (account.username === 'USD' || account.username === 'GBP' || account.country === 'cc')) {
        const exchangInfo = usePurchaseStore().getCurrentExchange;
        currencyCode.value = exchangInfo.name;
    } else {
        currencyCode.value = country.value.currency;
    }
    
   
    isFormValid.value = false;
}

async function setupPaysafe() {
    emit('onLoading', true);
    const selectedPayment = payment_methods.value.find((obj: any) => obj.abbr === 'paysafe2/CREDITCARD');

    if (!selectedPayment) {
        emit('onLoading', false);
        setError('Paysafe method not found');
        return false;
    }

    if (!cart) {
        emit('onLoading', false);
        setError('No amount set...');
        return false;
    }

    const API_KEY = selectedPayment.refresh_token;
    const options = getConfig();
    paysafe.fields
        .setup(API_KEY, options)
        .then((instance: any) => {
            console.log('Setup instance completed.');
            paysafeInstance = instance;
            emit('onLoading', false);
            return instance.show();
        })
        .then((paymentMethods: any) => {
            if (paymentMethods.card && !paymentMethods.card.error) {
                addEventListeners(paysafeInstance);
            }
            return true;
        }).catch((error: any) => {
            // display any setup errors
            emit('onLoading', false);    
            console.log('Error', error);     
            setError(error.displayMessage);
            return false;
        });

    return true;
}

watch(meta, async () => {   
    setBillingAddressValid();
});


const total = computed(() => {
    if (scenarioSelected.value) {        
        return scenarioSelected.value.input.amount;
    } else {
        if (cart?.value) {
            return cart.value.reduce((calc: number, obj: any) => {
                return calc + obj.price * obj.amount;
            }, 0);
        } else {
            return 1000;
        }
    }
});

const credits = computed(() => {
    if (scenarioSelected.value) {
        return scenarioSelected.value.input.amount;
    } else {
        if (cart?.value) {
            return cart.value.reduce((calc: number, obj: any) => {
                return calc + obj.quantity * obj.amount;
            }, 0);
        } else {
            return 1000;
        }
    }
    
});

const getConfig = () => {
    let currency = currencyCode.value ? currencyCode.value : CURRENCY_CODE; 
   
    const environment = ENVIRONMENT_MODE;  //TEST or LIVE

    let options: any = {
        currencyCode: currency,
        environment: environment,
        fields: {
            cardNumber: {
                selector: "#cardNumber",
                placeholder: "****************",
                separator: " "
            },
            cvv: {
                selector: "#cardCVC",
                placeholder: "****",
                mask: false,
                optional: false
            },
            expiryDate: {
                selector: "#cardExpiry",
                placeholder: "**/**"
            }
        },
        style: {
            input: {
                "font-family": "robotoregular,Helvetica,Arial,sans-serif",
                "font-weight": "normal",
                "font-size": "14px",
                "line-height": "14px",
                "color": "#aaa"
            },
            ".valid:focus": {
                color: "#aaa"
            },
            ":focus": {
                "color": "#000"
            }
        }
    };

    //on live..
    if (!isTesting.value) {
        options.accounts = {
            default: +getAccountNumber(currency) //config thingy and we have to check the site... maybe backend ??
        }
    }

    

    return options;
}

const getAccountNumber = (currencyCode: string) => {
    switch (currencyCode) {
        case 'EUR':
            return `${config.CCEUR}`;
        case 'USD':
            return `${config.CCUSD}`;
        case 'GBP':
            return `${config.CCGBP}`;
        default:
            return `${config.CCEUR}`;
    }
}


const countriesModel = computed(() => {
    if (filter.value && filter.value.length > 0) {
        const filteredCountries = countries.filter((p) => p.name.toLowerCase().startsWith(filter.value.toLowerCase()));
        if (filteredCountries.length === 0) {
            return countries;
        }

        return filteredCountries;
    } else {
        return countries;
    }
})

function selectCountry(option: any) {
    //reset filter
    filter.value = '';
    country.value = option;
    stateCA.value = '';
    stateUS.value = '';
    state.value = '';
   
    if (country.value) {      
        needsBillingInfo.value = country.value.extra; 
        //currencyCode.value = country.value.currency;
    } 
}

function filterCountry(value: string) {
    filter.value = value;
}

async function onSubmit(event: Event) {
    clearError();
    isSubmitting.value = true;

    event.preventDefault();   
    activeValidation.value = true;

    //check complete form...
    if (!isBillingValid.value) {        
        setError('Invalid payment input');
        isSubmitting.value = false;
        return false;
    }

    if (!isCardValid.value) {                
        setError('Invalid creditcard input');
        isSubmitting.value = false;
        return false;
    }
  
    const result = await tokenize(paysafeInstance);
    if (result) {
        activeValidation.value = false;  
        setTimeout(() => {
            close();
        }, CLOSE_TIMEOUT_SEC * 1000);
    } 
    
    isSubmitting.value = false;
    return result;
}

function setCardValid(): void {
    if (paysafeInstance) {        
        isCardValid.value = paysafeInstance.areAllFieldsValid();
        isFormValid.value = isBillingValid.value && isCardValid.value;    
    } else {
        isCardValid.value = false;
    }
}

function setBillingAddressValid(): void {
    isBillingValid.value = meta.value ? meta.value.valid : false;
    isFormValid.value = isBillingValid.value && isCardValid.value;
}

function close() {
    closeModal('paysafe');
}
 
async function tokenize(paysafeInstance: any, with3DS = true) {
    if (!isCardValid.value) {
        setError('Invalid creditcard input');
        return false;
    }

    //validate form...
    if (!isBillingValid.value) {
        setError('Invalid payment input');
        return false;
    } 

    const billingDetails: any = {
        country: country.value.code,
        zip: zipcode.value       
    }

    if (street.value && street.value != '') {
        billingDetails.street = street.value;
    }

    if (city.value && city.value != '') {
        billingDetails.city = city.value;
    }

    if (state.value && state.value != '') {
        billingDetails.state = state.value;
    }

    if (stateCA.value && stateCA.value != '') {
        billingDetails.state = stateCA.value;
    }

    if (stateUS.value && stateUS.value != '') {
        billingDetails.state = stateUS.value;
    }

    const amount = total.value; //get exchanged amount...

    const ThreeDSOption = {
        merchantUrl: `${config.FullApiUrl}payment`,
        deviceChannel: 'BROWSER',
        useThreeDSecureVersion2: true,
        authenticationPurpose: 'PAYMENT_TRANSACTION',
        messageCategory: 'PAYMENT',        
        transactionIntent: 'GOODS_OR_SERVICE_PURCHASE'          
    }
    const locale = `en_${country.value && ['GB', 'CA', 'US'].indexOf(country.value) >= 0 ? country.value : 'US'}`;

    const account = useUserStore().account;
    const emailAddress = account.email ? account.email : 'unknown@unknown.com';
    const phoneNumber = account.mobileNumber;
    
    const profile: any = {
        "locale": locale,
        "firstName": firstName.value,
        "lastName": lastName.value,
        "email": emailAddress
    }

    if (phoneNumber) {
        profile.phoneNumber = phoneNumber;
    }

    const holderName = `${firstName.value} ${lastName.value}`

    let tokenizationOptions: any = {
        amount: filters.exchangeAmount(amount), //get exchanged amount
        transactionType: 'PAYMENT',
        paymentType: 'CARD',
        merchantRefNum: 'ref:' + new Date().getTime(),
        merchantDescriptor: {
            dynamicDescriptor: merchantName.value
        },
        customerDetails: {
            holderName: holderName,
            billingDetails: billingDetails,
            profile: profile
        },
        openAs: 'IFRAME'
    };

  

    if (with3DS) {
        tokenizationOptions.threeDs = ThreeDSOption;
        tokenizationOptions.vault = {
            holderName: holderName
        };
    }

    const currency = currencyCode.value ? currencyCode.value : 'EUR';
    const accountId = +getAccountNumber(currency);
    if (!isTesting.value) {
        tokenizationOptions.accountId = accountId;
    }

    if (paysafeInstance) {
        isProcessing.value = true;
        return paysafeInstance
            .tokenize(tokenizationOptions)
            .then(async (resp: any, tokenizeError: any) => {
                // write the Payment token value to the browser console    
                if (tokenizeError) {
                    isProcessing.value = false;                    
                    console.error(`Tokenize error:`, tokenizeError);
                    setError(tokenizeError.displayMessage);
                    trackEvent('Paysafe', 'Transaction Failure', tokenizeError.displayMessage);
                    return false;
                }          

                const cartList = getCartList.value;
                

                const payload: any = { amount: amount, paymentHandleToken: resp.token, keywords: cartList };  
                
                if (!isTesting.value) {
                    payload.currency = currency;
                    payload.account_id = accountId;
                }                               

                const { result, error } = await finishPaySafePayment(payload);

                if (error) {      
                    isProcessing.value = false;   
                    console.error(`Finish payment error:`, error);
                    if (error.message === '"Payment fail!"') {
                          setError(`${i18n.global.t('modals.paysafe.errors.incorrect_payment_details')}`); 
                    } else {
                    
                          setError(`${i18n.global.t(translateErrorCode(error), {code: error.code})}`); 
                    }
                    
                    trackEvent('Paysafe', 'Transaction Failure', i18n.global.t(error.message)); 
                    return false;
                }

                clearCart();
                isProcessing.value = false;
                //redirect to thanks you page..     
                isFinished.value = true;     
                trackGoal(1, (total.value/100));
                trackEvent('Paysafe', 'Transaction Success');                                      
                return true;
            })
            .catch((error: any) => {
                //setError(error.displayMessage);                
                isProcessing.value = false;                
                isFinished.value = false;  
                console.error(`General tokenize error:`, error);
                setError(`${i18n.global.t(translateErrorCode(error), {code: error.code})}`);

                //try again without 3ds...
                trackEvent('Paysafe', 'Transaction Failure', error);
                return false;
            });
    } else {
        setError(`No paysafe instance found`);
        return false;
    }
}

function translateErrorCode(error: any): string {
    let errorPrefix = `modals.paysafe.errors`;
    console.log(error.displayMessage);
    if (error.code) {
        switch (+error.code) {
            case 9001:
            case 9002:
            case 9110:  
            case 9114:
            case 9131:
                //connection error try again later..
                return `${errorPrefix}.try_later`;
            case 9003:
            case 9037:
            case 9039:
            case 9040:
            case 9041: 
                //incorrect information
                return `${errorPrefix}.incorrect_payment_details`;
            case 9042:
                return `${errorPrefix}.aborted`;
            case 9015:
            case 9111:
            case 9073:
                return `${errorPrefix}.unavailable`; //configuration error let customer know service is unavailable...
            case 9125:
                return `${errorPrefix}.unsupported_brand`; 
            
        }
    }

    return `modals.paysafe.errors.general`
}


function changeCreditCardIcon(brand: string) {
    if (ccIcon.value) {

        const iconElement: HTMLElement = ccIcon.value;        
        //clear
        for (const item of iconElement.classList) {      
            iconElement.classList.remove(item);
        }

        if (brand === 'default') {    
            iconElement.classList.remove('fa-brands');        
            iconElement.classList.add('fa');
            iconElement.classList.add('fa-credit-card');
        } else {
            iconElement.classList.add('fa-brands');
            iconElement.classList.add(`fa-cc-${brand}`);                
        }
        
    }
}

function getFieldElement(fieldName: string) : HTMLElement | undefined {
    if (fieldName) {        
        switch (fieldName.toLowerCase()) {
            case 'cardnumber':                
                return cardNumber.value ? cardNumber.value as HTMLElement : undefined;                
            case 'addon':
                return cardAddon.value ? cardAddon.value as HTMLElement : undefined;                
            case 'expirydate':
                return cardExpiry.value ? cardExpiry.value as HTMLElement : undefined;                
            case 'cvv':
                return cardCVC.value ? cardCVC.value as HTMLElement : undefined;
            default:
                console.error('Could not find element', fieldName);
                return undefined;
        }      
    }

    return undefined;
}

function removeClass(fieldNames: string[] | undefined, className: string) {
    if (!fieldNames || fieldNames.length <= 0) {
        return false;
    }

    if (Array.isArray(fieldNames)) {
        for (const fieldName of fieldNames) {
            const element = getFieldElement(fieldName);

            if (!element) {
                continue;
            }        

            element.classList.remove(className);
        }

        return true;
    }

    return false;
}


function addClass(fieldNames: string[] | undefined, className: string) {
    if (!fieldNames || fieldNames.length <= 0) {
        return false;
    }

    if (Array.isArray(fieldNames)) {
        for (const fieldName of fieldNames) {
            const element = getFieldElement(fieldName);

            if (!element) {
                continue;
            }

            element.classList.add(className);
        }

        return true;
    }

    return false;
}


function addEventListeners(instance: any) {
    instance
        .fields("cvv cardNumber expiryDate")
        .valid(function (eventInstance: any, event: Event) {          
            setCardValid();  
            //focus new...    
            const fieldName: string = (event.target! as any).fieldName;            
            addClass(fieldName === 'CardNumber' ? [fieldName, 'addon'] : [fieldName], 'valid');
            removeClass(fieldName === 'CardNumber' ? [fieldName, 'addon'] : [fieldName], 'focus');
        });

    instance
        .fields("cvv cardNumber expiryDate")
        .invalid(function (eventInstance: any, event: Event) {
            setCardValid();
            //focus new...    
            const fieldName: string = (event.target! as any).fieldName;
            removeClass(fieldName === 'CardNumber' ? [fieldName, 'addon'] : [fieldName], 'valid');
            addClass(fieldName === 'CardNumber' ? [fieldName, 'addon'] : [fieldName], 'focus');
            
        });

    instance
        .fields("cvv cardNumber expiryDate")
        .focus(function (eventInstance: any, event: Event) {                            
            const fieldName: string = (event.target! as any).fieldName;
            addClass(fieldName === 'CardNumber' ? [fieldName, 'addon'] : [fieldName], 'focus');
        });

    instance
        .fields("cvv cardNumber expiryDate")
        .blur(function (eventInstance: any, event: Event) {
            const fieldName: string = (event.target! as any).fieldName;
            removeClass(fieldName === 'CardNumber' ? [fieldName, 'addon'] : [fieldName], 'focus');
        });


    instance.fields.cardNumber.on("FieldValueChange", function (instance: any, event: Event) {
        if (!instance.fields.cardNumber.isEmpty()) {  
            const cardBrandRaw = instance.getCardBrand();         
            const cardBrand = cardBrandRaw ? cardBrandRaw.replace(/\s+/g, "") : 'unknow';
   
            switch (cardBrand) {
                case "AmericanExpress":
                    changeCreditCardIcon('amex');
                    break;
                case "MasterCard":
                    changeCreditCardIcon('mastercard');
                    break;
                case "Visa":
                    changeCreditCardIcon('visa');
                    break;
                case "Diners":
                    changeCreditCardIcon('diners-club');
                    break;
                case "JCB":
                    break;
                case "Maestro":
                    changeCreditCardIcon('discover');
                    break;
                default:
                    changeCreditCardIcon('default');
                    break;
            }
        } else {
            changeCreditCardIcon('default');
        }
    });
}

function setError(msg: string) {
    errors.value.message =  msg;
    hasErrors.value = true;
}

function clearError() {
    errors.value.message = undefined;
    hasErrors.value = false;
}
</script>

<template src="./modal-paysafe.html"></template>
<style lang="scss">
@import './modal-paysafe.scss';
</style>